/* global APPCUES_ACCOUNT_ID */
import { ErrorBoundary } from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import React, { Suspense, useEffect, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Helmet } from 'react-helmet-async';
import { Provider, useDispatch } from 'react-redux';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  useParams,
} from 'react-router-dom';
import styled from 'styled-components';

import { Provider as AppcuesProvider } from 'common/components/Appcues';
import HTTP500 from 'common/components/ErrorRoutes/HTTP500';
import ErrorRoutes from 'common/components/ErrorRoutes/Routes';
import Loading from 'common/components/Loading';
import TranslationProvider from 'common/components/Translation/Provider';
import useSearchParams from 'common/hooks/useSearchParams';
import { setPortal } from 'common/state/system';
import { APP_FONT_FAMILY } from 'common/styles';
import Portal from 'common/types/Portal';

import CookieConsent from '@components/CookieConsent';
import { Provider as UploadingDrawerProvider } from '@components/UploadingDrawer';
import ActivityFeedModeration from '@domains/ActivityFeed/Moderation';
import authRoutes, { PrivateRoute, StaffRoute } from '@domains/Auth';
import AcceptInvite from '@domains/Auth/AcceptInvite';
import ActivateAccount from '@domains/Auth/ActivateAccount';
import Capture from '@domains/Capture';
import CapturePDF from '@domains/Capture/PDF';
import Event from '@domains/Event';
import IntegrationOAuthRedirect from '@domains/Integration/OAuth';
import Leaderboard from '@domains/Leaderboard';
import LiveResultsPage from '@domains/LiveResults';
import LiveResultsModeration from '@domains/LiveResults/Moderation';
import ManageMobi from '@domains/ManageMobi';
import Organization from '@domains/Organization';
import Settings from '@domains/Organization/Organizer/Settings';
import store from '@state/store';

import 'chart.js/auto';
import './setup-sentry';

import Layout from './Layout';
import GlobalStyles from './overrides';
import RQDevTools from './RQDevTools';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
});

const Wrap = styled.div`
  height: 100vh;
  font-family: ${APP_FONT_FAMILY};

  & > p {
    margin: 0;
    padding: 0;
  }
`;

const AcceptInviteRedirect = () => {
  const query = useSearchParams();
  const organizationId = query?.get('organization_id');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const effect = async () => {
      setIsLoading(false);
    };

    effect();
  }, [organizationId]);

  if (isLoading) {
    return <Loading />;
  }

  return <AcceptInvite />;
};

const IntegrationOAuthRedirectFix = () => {
  // Auth from eventbrite doesn't come with a type specified
  const { type = 'eventbrite' } = useParams();
  const query = useSearchParams();
  const code = query?.get('code');
  const error = query?.get('error');

  return <Navigate to={`/oauth-redirect/${type}/${code}${error ? `/${error}` : ''}`} replace />;
};

const basename = '/';

// Root layout: owns Suspense (for the lazy route elements) and the portal
// dispatch, then renders the matched route. Applies to slim and full pages.
const RootLayout = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(setPortal(Portal.Experience));
  }, [dispatch]);

  return (
    <Suspense fallback={<Loading />}>
      <Outlet />
    </Suspense>
  );
};

/**
 * Providers for a typical (framed) page. The capture / pdf-capture pages
 * deliberately skip these — some of them render a blank page and break badge
 * printing, which is why the slim routes live outside this layout.
 */
const FullProvidersLayout = () => (
  <AppcuesProvider>
    <DndProvider backend={HTML5Backend}>
      <UploadingDrawerProvider>
        <Helmet defer={false}>
          <base href={basename} />
          <script type="text/javascript">
            {`window.AppcuesSettings = { enableURLDetection: true };`}
          </script>
          {APPCUES_ACCOUNT_ID && (
            <script async src={`//fast.appcues.com/${APPCUES_ACCOUNT_ID}.js`} />
          )}
        </Helmet>
        <Outlet />
        <CookieConsent />
      </UploadingDrawerProvider>
    </DndProvider>
  </AppcuesProvider>
);

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<RootLayout />}>
      {/* Slim pages: capture / pdf-capture get ONLY the base providers (from
          BaseProviders below). Do NOT move these under FullProvidersLayout —
          the extra providers break badge printing. */}
      <Route path="/event/:eventId/capture/:resourceType/:resourceId" element={<Capture />} />
      <Route path="/event/:eventId/pdf-capture/*" element={<CapturePDF />} />

      {/* Full pages: everything else, wrapped in the typical-page providers. */}
      <Route element={<FullProvidersLayout />}>
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route
          path="/settings/:tab"
          element={
            <PrivateRoute>
              <Layout>
                <Settings />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/event/:eventId/activity-feed/moderation/*"
          element={<ActivityFeedModeration />}
        />
        <Route
          path="/organization/:organizationId/event/:eventId/*"
          element={
            <PrivateRoute>
              <Layout>
                <Event />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/organization/:organizationId/*"
          element={
            <PrivateRoute>
              <Layout>
                <Organization />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/organization"
          element={
            <PrivateRoute>
              <Layout>
                <Organization />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/event/:eventId/:shortcode/gamification-leaderboard/:hashVal"
          element={<Leaderboard />}
        />

        {/* moderate-live-results: 3 path variations from the v5 array path */}
        <Route
          path="/event/:eventId/question/moderate-live-results/:hash/:questionType/:sessionId"
          element={<LiveResultsModeration />}
        />
        <Route
          path="/event/:eventId/question/:questionId/moderate-live-results/:hash/:questionType/:sessionId"
          element={<LiveResultsModeration />}
        />
        <Route
          path="/event/:eventId/question/:questionId/moderate-live-results/:hash/:questionType"
          element={<LiveResultsModeration />}
        />

        {/* live-results: 3 path variations from the v5 array path */}
        <Route
          path="/event/:eventId/question/:questionId/live-results/:hash/:questionType/:sessionId"
          element={<LiveResultsPage />}
        />
        <Route
          path="/event/:eventId/question/:questionId/live-results/:hash/:questionType"
          element={<LiveResultsPage />}
        />
        <Route
          path="/event/:eventId/question/live-results/:hash/:questionType/:sessionId"
          element={<LiveResultsPage />}
        />

        <Route
          path="/managemobi/*"
          element={
            <StaffRoute>
              <Layout>
                <ManageMobi />
              </Layout>
            </StaffRoute>
          }
        />
        <Route path="/accept-invite" element={<AcceptInviteRedirect />} />

        {/* /oauth-redirect with optional :type — split into 2 routes */}
        <Route path="/oauth-redirect/:type" element={<IntegrationOAuthRedirectFix />} />
        <Route path="/oauth-redirect" element={<IntegrationOAuthRedirectFix />} />

        {/* /oauth-redirect/:type/:code with optional :error — split into 2 routes */}
        <Route path="/oauth-redirect/:type/:code/:error" element={<IntegrationOAuthRedirect />} />
        <Route path="/oauth-redirect/:type/:code" element={<IntegrationOAuthRedirect />} />

        <Route path="/activate" element={<ActivateAccount />} />
        {authRoutes}
        {ErrorRoutes}
      </Route>
    </Route>,
  ),
  { basename },
);

/**
 * Router-independent providers shared by every page (slim and full). They wrap
 * the RouterProvider so the slim capture pages get exactly these and nothing
 * more.
 */
const BaseProviders = ({ children }) => (
  <Wrap>
    <GlobalStyles />
    <TranslationProvider>
      <ErrorBoundary fallback={<HTTP500 />}>
        <QueryClientProvider client={queryClient}>
          <RQDevTools />
          <Provider store={store}>{children}</Provider>
        </QueryClientProvider>
      </ErrorBoundary>
    </TranslationProvider>
  </Wrap>
);

BaseProviders.propTypes = {
  children: PropTypes.node.isRequired,
};

const App = () => (
  <BaseProviders>
    {/* v7_startTransition is a RouterProvider flag, not a createBrowserRouter
        option — it's silently ignored there. */}
    <RouterProvider router={router} future={{ v7_startTransition: true }} />
  </BaseProviders>
);

export default App;
